import { Button, Card, Table, TableBody, TableContainer, Container } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react'
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs/custom-breadcrumbs';
import Iconify from 'src/components/iconify';
import { useSettingsContext } from 'src/components/settings';
import { LoadingScreen } from 'src/components/loading-screen';
import Scrollbar from 'src/components/scrollbar';
import { TableNoData, TablePaginationCustom, useTable, TableHeadCustom } from 'src/components/table';
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import { RoleBasedGuard } from 'src/auth/guard';
import { useAuthContext } from 'src/auth/hooks';
import { RouterLink } from 'src/routes/components';
import { useGetAllTourDealsMutation } from 'src/services/affordable/tour-deals/tour-deals-api';
import { useBoolean } from 'src/hooks/use-boolean';
import TourDealsTableRow from './tour-deals-table-row';
import AffordableTableToolbar from './affordable-table-toolbar';
import TourDealForm from './tour-deal-form';
import ErrorView from '../error/error-view';

const TABLE_HEAD = [
    { id: 'deal', label: 'Tour Deal' },
    { id: 'package', label: 'Tour Package'},
    { id: 'discount', label: 'Discount'},
    { id: 'period', label: 'Period'},
    { id: 'action', label: ''}
];

const defaultFilters = {
    name: '',
    ref: ''
};

export default function TourDealsList() {
    const settings = useSettingsContext();

    const router = useRouter();

    const table = useTable({ defaultOrderBy: 'name' });

    const [tableData, setTableData] = useState([]);
    const [totalElements, setTotalElements] = useState(0);

    const [filters, setFilters] = useState(defaultFilters);
    const [searchTimer, setSearchTimer] = useState(null);
    const [searchKey, setSearchKey] = useState(null);
    const [isShow, setIsShow] = useState(false);

    const { isUserRoleHasAccess } = useAuthContext();

    const [getAllTourDeals, { isLoading, isSuccess, data, error }] = useGetAllTourDealsMutation();

    const canReset =
        !!filters.name ||
        !!filters.ref;

    const notFound = (!tableData.length && canReset) || !tableData.length;

    const quickCreate = useBoolean();
    const quickEdit = useBoolean();
    const [viewData, setViewData] = useState(null);

    const handleResetFilters = useCallback(() => {
        setSearchKey(null);
        setFilters(defaultFilters);
    }, []);

    const handleFilters = useCallback((name, value) => {
        table.onResetPage();

        setSearchKey(value);

        if (searchTimer) {
            clearTimeout(searchTimer);
        }

        if (!value) {
            handleResetFilters();
        }

        const newSearchTimer = setTimeout(() => {
            getAllTourDeals({ page: table.page, size: table.rowsPerPage, key: value });
        }, 1000);

        setSearchTimer(newSearchTimer);  
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[handleResetFilters, searchTimer, table]);

    useEffect(() => {
        getAllTourDeals({ page: table.page, size: table.rowsPerPage, key: searchKey });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getAllTourDeals, table.page, table.rowsPerPage]);

    const handleCreateView = useCallback(() => {
        quickCreate.onTrue();
    },[quickCreate]);

    const handleEditRow = useCallback((row) => {
        if(!quickEdit.value){
            setViewData(row);
            quickEdit.onTrue();
        }
    },[quickEdit]);

    const handleViewRow = useCallback(
        (row) => {
            const dataObject = {
                tourDeal: row
            }
            router.pushData(paths.dashboard.affordable.viewTourDeals(row.id), dataObject);
        },
        [router]
    );

    const handleInquiryRow = useCallback(
        (row) => {
            const dataObject = {
                inquiryType: 'FROM_DEAL',
                packOrDealId: row.id
            }
            router.pushData(paths.dashboard.inquiry.createInquiry, dataObject);
        },
        [router]
    );

    useEffect(() => {
        if (isSuccess) {
            setTableData(data.content)
            setTotalElements(data.totalElements);
        }
    }, [isSuccess, data]);

    const editRefreshCall = useCallback(() => {
        handleResetFilters();
        getAllTourDeals({ page: table.page, size: table.rowsPerPage, key: searchKey });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handleResetFilters]);

    let partnersTypeContent;
    if (isLoading) {
        partnersTypeContent = <LoadingScreen />
    }
    else if (error) {
        partnersTypeContent = <ErrorView error={error?.data} showTryAgain={false}/>
    }
    else if (isSuccess) {
        partnersTypeContent = (
            <Card>
                <AffordableTableToolbar
                    value={searchKey}
                    onFilters={handleFilters}
                />

                <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
                    <Scrollbar>
                        <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 800 }} stickyHeader>
                            <TableHeadCustom
                                order={table.order}
                                orderBy={table.orderBy}
                                headLabel={TABLE_HEAD}
                                rowCount={tableData.length}
                                numSelected={table.selected.length}
                                onSort={table.onSort}
                                onSelectAllRows={(checked) =>
                                table.onSelectAllRows(
                                checked,
                                tableData.map((row) => row.id)
                                )}
                            />

                            <TableBody>
                                {tableData
                                    .map((row) => (
                                        <TourDealsTableRow
                                            key={row.id}
                                            row={row}
                                            onViewRow={() => handleViewRow(row)}
                                            onEditRow={() => handleEditRow(row)}
                                            onInquiryRow={() => handleInquiryRow(row)}
                                        />
                                    ))}
                                <TableNoData notFound={notFound} />
                            </TableBody>
                        </Table>
                    </Scrollbar>
                </TableContainer>

                {tableData.length > 0 &&
                    <TablePaginationCustom
                        count={totalElements}
                        page={table.page}
                        rowsPerPage={table.rowsPerPage}
                        onPageChange={table.onChangePage}
                        onRowsPerPageChange={table.onChangeRowsPerPage}
                        dense={table.dense}
                        onChangeDense={table.onChangeDense}
                    />}
            </Card>
        );
    }

    return (
        <>
            <Container maxWidth={settings.themeStretch ? false : 'lg'}>
                <CustomBreadcrumbs
                    heading="Tour Deals"
                    links={[
                        {
                            name: 'Dashboard',
                            href: paths.dashboard.driverReview.root,
                        },
                        {
                            name: 'Tour Deals',
                            href: paths.dashboard.affordable.tourDeals,
                        },
                        {
                            name: 'List'
                        },
                    ]}
                    action={
                        isUserRoleHasAccess(3, 1)
                        ?
                        <Button
                            component={RouterLink}
                            // href={paths.dashboard.affordable.createTourDeals}
                            onClick={handleCreateView}
                            variant="contained"
                            startIcon={<Iconify icon="mingcute:add-line" />}
                        >
                            Add Tour Deal
                        </Button>
                        :
                        <></>
                    }
                    sx={{
                        mb: { xs: 3, md: 5 },
                    }}
                />

                <RoleBasedGuard hasContent mainMenuOrder={3} subMenuOrder={1} sx={{ py: 10 }}>
                    {partnersTypeContent}
                </RoleBasedGuard>
            </Container>
            
            <TourDealForm open={quickCreate.value} onClose={quickCreate.onFalse} onSuccess={editRefreshCall}/>
            <TourDealForm tourDeal={viewData} open={quickEdit.value} onClose={quickEdit.onFalse} onSuccess={editRefreshCall}/>
        </>
    );
}
